// library imports
import { Empty, message } from "antd";
import { useEffect, useState, useContext, useMemo } from "react";
import dayjs from "dayjs";
// component imports
import Analytics from "../../components/analytics/Analytics";
import CustomLineBarChart from "../../components/common/CustomLineBarChart";
import { VerticalDateFilter } from "../../components/common/DateFilter";
// context imports
import { IndexContext } from "../../context/IndexContext";
// hook imports
import useFetch from "../../hooks/useFetch";
// config imports
import {
  DARK_BLUE,
  LIGHT_BLUE,
  LIGHT_LIGHT_BLUE,
  DARK_GREEN,
  LIGHT_GREEN,
  ORANGE,
  DARK_THEME_DARK_BLUE,
  PURPLE,
} from "../../configs/configs";

const dateTypeOptions = [
  { label: "Day", value: "date" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

export default function PowerDemandAndForecastPage() {
  const [fetchWrapper] = useFetch();
  const {
    siteSelected,
    notificationApi,
    isLightTheme,
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
  } = useContext(IndexContext);

  // Frontend-defined config: structure and colors for each forecast line.
  // Keyed by `key` — order and presence in forecastData are independent.
  const ADDITIONAL_LINES_CONFIG = useMemo(
    () => [
      {
        key: "threshold",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: "#f5222d",
      },
      {
        key: "tnb24h",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: "#722ed1",
      },
      {
        key: "tnb1h",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: isLightTheme ? "#531dab" : "#b37feb",
      },
      {
        key: "pv24h",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: "#d4b106",
      },
      {
        key: "pv1h",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: isLightTheme ? "#ad6800" : "#fadb14",
      },
      {
        key: "load24h",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: "#eb2f96",
      },
      {
        key: "load1h",
        xAxis: "date",
        yAxis: "value",
        colorKey: "type",
        color: "#ffadd2",
      },
    ],
    [isLightTheme],
  );

  const [isTrendingDataLoading, setIsTrendingDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const getNextHalfHour = () => {
    const now = dayjs();

    const minutes = now.minute();
    const remainder = minutes % 30;

    // if already exactly on boundary and no seconds, keep it
    const addMinutes =
      remainder === 0 && now.second() === 0 ? 0 : 30 - remainder;

    return now.add(addMinutes, "minute").second(0).millisecond(0);
  };

  const end = getNextHalfHour();

  const [endDateSelection, setEndDateSelection] = useState(end);
  const [startDateSelection, setStartDateSelection] = useState(
    end.subtract(1, "day"),
  );

  const [dateTypeSelection, setDateType] = useState(dateTypeOptions[0].value);

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  const [powerDemandData, setPowerDemandData] = useState([]);
  const [loadData, setLoadData] = useState([]);

  // Data keyed by the same keys as ADDITIONAL_LINES_CONFIG.
  // TODO: replace mock arrays with API response data.
  const [forecastData, setForecastData] = useState({
    threshold: [],
    tnb24h: [],
    tnb1h: [],
    pv24h: [],
    pv1h: [],
    load24h: [],
    load1h: [],
  });

  const [chartRange, setChartRange] = useState([]);
  const [chartDomain, setChartDomain] = useState([]);

  const onFilterClick = () => {
    if (startDateSelection >= endDateSelection) {
      message.error("End date must be later than start date.");
    } else if (endDateSelection.diff(startDateSelection, "day") > 31) {
      message.warning("Date range cannot exceed 31 days.");
    } else {
      getPowerDemandData();
    }
  };

  const onExportClick = () => {
    if (powerDemandData.length < 1) {
      message.error("No data to export");
    } else {
      if (powerDemandData.length < 1 && loadData.length < 1) {
        message.error("No data to export");
      } else {
        downloadPowerDemandData();
      }
    }
  };

  /** API to get the power demand data */
  function getPowerDemandData() {
    setIsTrendingDataLoading(true);
    fetchWrapper({
      endpoint_url: "power_demand_and_forecast/getPowerDemandAndForecastData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: startDateSelection.format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDateSelection.format("YYYY-MM-DD HH:mm:ss"),
        date_selection:
          dateTypeSelection === "date" ? "day" : dateTypeSelection,
      },

      onSuccess: (response) => {
        setPowerDemandData(response.data.power_demand_trending_data);
        setLoadData(response.data.load_data);
        setForecastData({
          threshold: response.data.threshold,
          pv1h: response.data.pv1h,
          load1h: response.data.load1h,
          tnb1h: response.data.tnb1h,
          pv24h: response.data.pv24h,
          load24h: response.data.load24h,
          tnb24h: response.data.tnb24h,
        });
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get power demand data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setIsTrendingDataLoading(false);
      },
    });
  }

  /** API to download power demand data as excel */
  function downloadPowerDemandData() {
    setDownloadLoading(true);

    fetchWrapper({
      endpoint_url:
        "power_demand_and_forecast/downloadPowerDemandAndForecastData",
      method: "POST",
      json_output: false,
      body: {
        power_demand_data: powerDemandData,
        load_data: loadData,
        forecast_data: forecastData,
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Power_Demand_And_Forecast_Data_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: () => {
        notificationApi.error({ message: "Failed to download file" });
      },

      onFinish: () => {
        setDownloadLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: { site_id: siteSelected },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  useEffect(() => {
    setPowerDemandData([]);
    setLoadData([]);
  }, [siteSelected]);

  useEffect(() => {
    let baseDomain = [];
    let baseRange = [];
    if (subscribeAiBess === true) {
      if (hasGensetSelected === true) {
        if (hasTnbSelected === false) {
        baseDomain = [
          "PV (Self-Consumption)",
          "PV (Export)",
          "BESS Discharge",
          "BESS Charge",
          "Load",
          "Genset",
        ];
        baseRange = isLightTheme
          ? [
              DARK_GREEN,
              LIGHT_GREEN,
              LIGHT_BLUE,
              LIGHT_LIGHT_BLUE,
              ORANGE,
              PURPLE,
            ]
          : [
              DARK_GREEN,
              LIGHT_GREEN,
              LIGHT_BLUE,
              LIGHT_LIGHT_BLUE,
              ORANGE,
              PURPLE,
            ];
        } else {
          baseDomain = [
          "PV (Self-Consumption)",
          "PV (Export)",
          "BESS Discharge",
          "BESS Charge",
          "TNB",
          "Load",
          "Genset",
        ];
        baseRange = isLightTheme
          ? [
              DARK_GREEN,
              LIGHT_GREEN,
              LIGHT_BLUE,
              LIGHT_LIGHT_BLUE,
              DARK_BLUE,
              ORANGE,
              PURPLE,
            ]
          : [
              DARK_GREEN,
              LIGHT_GREEN,
              LIGHT_BLUE,
              LIGHT_LIGHT_BLUE,
              DARK_THEME_DARK_BLUE,
              ORANGE,
              PURPLE,
            ];
        }
        
      } else {
        baseDomain = [
          "PV (Self-Consumption)",
          "PV (Export)",
          "BESS Discharge",
          "BESS Charge",
          "TNB",
          "Load",
        ];
        baseRange = isLightTheme
          ? [
              DARK_GREEN,
              LIGHT_GREEN,
              LIGHT_BLUE,
              LIGHT_LIGHT_BLUE,
              DARK_BLUE,
              ORANGE,
            ]
          : [
              DARK_GREEN,
              LIGHT_GREEN,
              LIGHT_BLUE,
              LIGHT_LIGHT_BLUE,
              DARK_THEME_DARK_BLUE,
              ORANGE,
            ];
      }
    } else {
      if (hasGensetSelected === true) {
        baseDomain = [
          "PV (Self-Consumption)",
          "PV (Export)",
          "TNB",
          "Load",
          "Genset",
        ];
        baseRange = isLightTheme
          ? [DARK_GREEN, LIGHT_GREEN, DARK_BLUE, ORANGE, PURPLE]
          : [DARK_GREEN, LIGHT_GREEN, DARK_THEME_DARK_BLUE, ORANGE, PURPLE];
      } else {
        baseDomain = ["PV (Self-Consumption)", "PV (Export)", "TNB", "Load"];
        baseRange = isLightTheme
          ? [DARK_GREEN, LIGHT_GREEN, DARK_BLUE, ORANGE]
          : [DARK_GREEN, LIGHT_GREEN, DARK_THEME_DARK_BLUE, ORANGE];
      }
    }

    // Append additional line types/colors from config + current data
    const additionalTypes = ADDITIONAL_LINES_CONFIG.map((cfg) => {
      const data = forecastData[cfg.key] ?? [];
      return data[0]?.[cfg.colorKey ?? "type"];
    }).filter(Boolean);
    const additionalColors = ADDITIONAL_LINES_CONFIG.map((cfg) => cfg.color);

    const typeToColor = {};
    baseDomain.forEach((type, i) => (typeToColor[type] = baseRange[i]));
    additionalTypes.forEach(
      (type, i) => (typeToColor[type] = additionalColors[i]),
    );

    const fullDomain = Array.from(new Set([...baseDomain, ...additionalTypes]));
    setChartDomain(fullDomain);
    setChartRange(fullDomain.map((type) => typeToColor[type]));
  }, [subscribeAiBess, hasGensetSelected, hasTnbSelected, isLightTheme, forecastData]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  useEffect(() => {
    if (siteCommissioningDate) {
      setStartDateSelection(
        dayjs(
          Math.max(
            end.subtract(1, "day"),
            dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
          ),
        ),
      );
      setEndDateSelection(end);
    }
  }, [siteCommissioningDate]);

  return (
    <Analytics
      title={"Power Demand Distribution + Forecast"}
      contentComponent={
        powerDemandData.length > 0 ? (
          <CustomLineBarChart
            dataLine={loadData.map((item) => {
              const newItem = { ...item };
              for (const key in newItem) {
                if (newItem[key] === "-") newItem[key] = null;
              }
              return newItem;
            })}
            dataBar={powerDemandData.map((item) => {
              const newItem = { ...item };
              for (const key in newItem) {
                if (newItem[key] === "-") newItem[key] = null;
              }
              return newItem;
            })}
            xAxisLine="date"
            yAxisLine="value"
            xAxisBar="date"
            yAxisBar="value"
            xAxisTitleBar="Time"
            yAxisTitleBar="Power Demand (kW)"
            lineColor="type"
            barColor="type"
            barChartRange={chartRange}
            barChartDomain={chartDomain}
            lineChartColor={ORANGE}
            additionalLinesConfig={ADDITIONAL_LINES_CONFIG}
            additionalLinesData={Object.fromEntries(
              Object.entries(forecastData).map(([key, arr]) => [
                key,
                arr.map((item) => {
                  const newItem = { ...item };
                  for (const k in newItem) {
                    if (newItem[k] === "-") newItem[k] = null;
                  }
                  return newItem;
                }),
              ]),
            )}
          />
        ) : (
          <Empty />
        )
      }
      isTrendingDataLoading={isTrendingDataLoading}
      filterComponent={
        <div className="analytics-picker-type-segmented">
          <VerticalDateFilter
            startDateSelection={startDateSelection}
            setStartDateSelection={setStartDateSelection}
            endDateSelection={endDateSelection}
            setEndDateSelection={setEndDateSelection}
            searchLoading={isTrendingDataLoading}
            downloadLoading={downloadLoading}
            onFilterClick={onFilterClick}
            onExportClick={onExportClick}
            pickerType={dateTypeSelection}
            minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
            maxDate={dayjs()}
            showTime={true}
            autoCorrectStart={false}
            autoCorrectEnd={false}
          />
        </div>
      }
    />
  );
}
